import { MAT_DATE_LOCALE, provideNativeDateAdapter, ErrorStateMatcher } from '@angular/material/core';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { FormsModule, NgControl, AbstractControl } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { StateService } from '../../../services/state.service';
import { MatInputModule } from '@angular/material/input';
import { Component, OnInit, Self } from '@angular/core';

class DateErrorStateMatcher implements ErrorStateMatcher {
    constructor(private stateService: StateService) {}

    isErrorState(control: AbstractControl | null): boolean {
        if (!control) {
            return false;
        }

        if (this.stateService.formIsSubmitted) {
            return control.invalid;
        }

        if (control.errors) {
            return Object.keys(control.errors).some((key) => key !== 'required');
        }

        return false;
    }
}

@Component({
  selector: 'minster-date-picker',
  standalone: true,
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
  imports: [MatFormFieldModule, MatDatepickerModule, MatInputModule, FormsModule],
  templateUrl: './minster-date-picker.component.html',
  styleUrl: './minster-date-picker.component.css'
})

export class MinsterDatePickerComponent extends ControlBaseComponent<string> implements OnInit {
    _minDate: Date;
    _maxDate: Date;

    errorStateMatcher: DateErrorStateMatcher;

    constructor(@Self() public controlDir: NgControl, private stateService: StateService) {
        super(controlDir, stateService);

        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 100);
        this._minDate = minDate;
        this._maxDate = new Date();

        this.errorStateMatcher = new DateErrorStateMatcher(stateService);
    }

    ngOnInit(): void {
        this.setup();
        this.controlDir.control?.setValidators([
            this.dateWithinRangeValidator.bind(this),
            this.dateFormatValidator.bind(this)
        ]);
        this.controlDir.control?.updateValueAndValidity();
    }

    onDateUpdated(newDate: Date | null): void {
        if (newDate) {
            this._value = newDate.toISOString();
            this._propagateChange(this._value);
        } 
        else {
            this._value = '';
            this._propagateChange(null);
        }

        this._propagateTouched();
    }

    get hasError(): boolean {
        return this.errorStateMatcher.isErrorState(this.controlDir?.control);
    }

    get errorMessage(): string {
        if (this.controlDir?.control?.hasError('required')) {
            return 'This field is required';
        }

        if (this.controlDir?.control?.hasError('dateOutOfRange')) {
            return 'The date must be within the last 100 years';
        }

        if (this.controlDir?.control?.hasError('invalidDateFormat')) {
            return 'Invalid date format';
        }

        return '';
    }

    get isRequired(): boolean {
        return this.stateService.formIsSubmitted;
    }

    private dateWithinRangeValidator(control: AbstractControl): { [key: string]: boolean } | null {
        if (!control.value) {
            return null;
        }

        const selectedDate = new Date(control.value);
        if (selectedDate < this._minDate || selectedDate > this._maxDate) {
            return { dateOutOfRange: true };
        }

        return null;
    }

    private dateFormatValidator(control: AbstractControl): { [key: string]: boolean } | null {
        if (!control.value) {
            return null;
        }

        const selectedDate = new Date(control.value);
        if (isNaN(selectedDate.getTime())) {
            return { invalidDateFormat: true };
        }

        return null;
    }
}